import { type Option } from "@tento-lona/sheets";
import { RowLocalId } from "@tento-lona/sheets";
import { CellGroup } from "@tento-ui/components/sheet/cell-group";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { rowIdentity } from "@tento-ui/components/sheet/row-identity";
import { SheetCell } from "@tento-ui/components/sheet/sheet-cell";
import { SpanCell } from "@tento-ui/components/sheet/span-cell";
import { sheetColumnsOf } from "./sheet-columns-of.ts";
import type { SheetDelegate } from "./sheet-delegate.ts";
import type { SheetBinder } from "./sheet-index-renderer.ts";
import type { SheetLayoutPort } from "@tento-ui/components/sheet/sheet-layout";
import type { SheetViewModel } from "@tento-lona";

/**
 * The sheet element's cell targeting (F11): which rendered cell (or fixed
 * child's cell) a `CellPosition` names, and where a static child sits for a
 * row. Moved out of sheet-element.ts unchanged; the element's sheet, layout
 * and static-child handle are read through the host when a method runs.
 */
export class SheetCellTargets {
  constructor(readonly host: SheetCellTargets.Host) {}

  cell(position: SheetCellTargets.CellPosition): Option<SheetCell> {
    const resolved = this.resolveCellTarget(position);
    if (!resolved) return null;
    return resolved.$targetCell;
  }

  resolveCellTarget(position: SheetCellTargets.CellPosition): Option<{
    $parentCell: SheetCell;
    $targetCell: SheetCell;
    $gridCell?: SheetCell;
  }> {
    // TODO: some positions can be IDs
    if (typeof position.col === "string") return null;

    const item = this.host.layout.columns.column(position.col);
    if (!item) {
      return this.#resolveFixedChildTarget(position);
    }
    const { $header, $body } = sheetColumnsOf(item);
    const $cells: SheetCell[] = [...$header.$cells(), ...$body.$cells()];
    const posRowStr = position.row.toString();
    const $parentCell = $cells.find(($c) => $c.cellId?.row === posRowStr);
    if (!$parentCell) {
      return this.#resolveFixedChildTarget(position);
    }

    // If the cell is in hidden span mode, decorations should target the
    // span cell's inner SheetCell instead of this invisible placeholder.
    // Carry $parentCell as $gridCell so measureFrame can anchor correctly.
    const spanMode = $parentCell.getAttribute("data-span-mode");
    if (spanMode === "hidden") {
      const fixed = this.#resolveFixedChildTarget(position);
      if (fixed) return { ...fixed, $gridCell: $parentCell };
      return { $parentCell, $targetCell: $parentCell };
    }

    // Non-stacked cells: return directly
    const $content = $parentCell.$content;
    if (!($content instanceof CellGroup) || !this.host.sheet) {
      return { $parentCell, $targetCell: $parentCell };
    }

    // For stacked cells (CellGroup), try to find the specific subcell
    const subcells = $content.getSubcells();

    // Try exact pd match first
    if (position.col instanceof ColumnIndex.Key) {
      const strategy = position.col.strategy;
      const inner = position.col.inner;
      if (strategy === "pd" || strategy === "pd-tz") {
        const targetPd = inner?.pd?.toString() ?? null;
        if (targetPd != null) {
          const byTargetPd = subcells.find(($sc) => {
            const scCol = $sc.cellId?.col;
            if (!scCol?.pd) return false;
            return scCol.pd.toString() === targetPd;
          });
          if (byTargetPd) {
            return { $parentCell, $targetCell: byTargetPd };
          }
        }
      }
    }

    // Resolve fallback target in sheet timescale
    const mapper = this.host.sheet.indexRenderer.mapper;
    const resolvedPosCol = mapper.resolveKeyToDefault(position.col, "lower");
    const byOffset = subcells.find(($sc) => {
      const scCol = $sc.cellId?.col;
      if (!scCol) return false;
      return scCol.offset === resolvedPosCol.offset;
    });
    if (byOffset) return { $parentCell, $targetCell: byOffset };

    // Fallback to pd match
    const byPd = subcells.find(($sc) => {
      const scCol = $sc.cellId?.col;
      if (!scCol) return false;
      return scCol.pd.toString() === resolvedPosCol.pd.toString();
    });
    if (byPd) return { $parentCell, $targetCell: byPd };

    return { $parentCell, $targetCell: $parentCell };
  }

  #resolveFixedChildTarget(position: SheetCellTargets.CellPosition): Option<{
    $parentCell: SheetCell;
    $targetCell: SheetCell;
  }> {
    if (typeof position.col === "string") return null;
    const mapper = this.host.sheet?.indexRenderer?.mapper;
    if (!mapper) return null;

    const { index } = mapper.indexOfKey(position.col);
    const $fixedChild = this.host.layout.children.fixed.findByRowAndOffset(
      rowIdentity(position.row.toString()),
      index,
    );

    // Non-static fixed children (SpanCell)
    if ($fixedChild instanceof SpanCell) {
      const $inner = $fixedChild.pluginCell;
      if ($inner instanceof SheetCell) {
        return { $parentCell: $inner, $targetCell: $inner };
      }
    }

    // Span-stacked fixed children (CellGroup) — find matching subcell by column
    if ($fixedChild instanceof CellGroup) {
      const resolvedCol = mapper.resolveKeyToDefault(position.col, "lower");
      const $subcell =
        $fixedChild
          .getSubcells()
          .find(($sc) => $sc.cellId?.col?.pd?.equals(resolvedCol.pd)) ?? null;
      if ($subcell) return { $parentCell: $subcell, $targetCell: $subcell };
    }

    // Static children (CellGroup.Row)
    const $staticChild = this.host.staticChildHandle.get("span", position.row);
    if ($staticChild instanceof CellGroup.Row) {
      const $inner = $staticChild.cell;
      return { $parentCell: $inner, $targetCell: $inner };
    }

    return null;
  }

  staticChildYPosition(
    rowId: RowLocalId,
    rowLayout: SheetCell.Layout,
  ): Option<{ px: number; anchor: "top" | "bottom" }> {
    if (!this.host.sheet) return null;

    const location = this.host.sheet.joinedTree.getRowLocation(rowId);
    if (!location) return null;

    if (location === "header") {
      return { px: rowLayout.offsetPx, anchor: "top" };
    }
    if (location === "footer") {
      // Footer row offsets are relative to the footer section start.
      // Use bottom positioning: distance from footer bottom to row bottom.
      // system(footer) → 0, disclaimer → height of row(s) below it.
      return {
        px:
          this.host.sheet.layout.footerRowsHeightPx -
          rowLayout.offsetPx -
          rowLayout.cellHeightPx,
        anchor: "bottom",
      };
    }
    return {
      px:
        rowLayout.offsetPx -
        this.host.sheet.layout.headerHeightPx +
        SheetCellTargets.topContentPaddingPx,
      anchor: "top",
    };
  }
}

/** Types and constants that go with `SheetCellTargets`. */
export namespace SheetCellTargets {
  /** What the targeting reads from its sheet element. */
  export type Host = {
    /** The bound sheet, if any. */
    readonly sheet: Option<SheetViewModel.Surface<SheetBinder>>;
    /** The layout surface the sheet drives. */
    readonly layout: SheetLayoutPort;
    /** The element's static-child handle. */
    readonly staticChildHandle: SheetDelegate.StaticChildHandle;
  };
  /** `SheetElement.CellPosition`. */
  export type CellPosition = { row: RowLocalId; col: ColumnIndex.Key | string };
  /** `SheetElement.topContentPaddingPx`. */
  export const topContentPaddingPx = 12;
}
